/**
 * Base WebSocket client for AsyncAPI-based communication
 *
 * Provides:
 * - Sequential trans_id management (0, 1, 2, ...)
 * - Request/response correlation
 * - Automatic reconnection with state restoration
 * - Type-safe message handling
 * - Observable streams for different message types
 */

import { Subject, BehaviorSubject, Observable } from "rxjs";
import { filter } from "rxjs/operators";
import type { ZodType } from "zod";

/**
 * Connection states for the WebSocket client
 */
export enum WebSocketClientState {
  /** Client has never connected */
  INITIAL = "INITIAL",
  /** Attempting to establish connection */
  CONNECTING = "CONNECTING",
  /** Connected and authenticated */
  CONNECTED = "CONNECTED",
  /** Connection lost, attempting to reconnect */
  RECONNECTING = "RECONNECTING",
  /** Disconnected intentionally */
  DISCONNECTED = "DISCONNECTED",
  /** Connection failed with unrecoverable error */
  FAILED = "FAILED",
}

/**
 * Base message interface - all messages must have a discriminator field
 */
export interface BaseMessage {
  message: string;
}

/**
 * Request message with trans_id for correlation
 */
export interface RequestMessage extends BaseMessage {
  trans_id: number;
}

/**
 * Response message with optional trans_id
 */
export interface ResponseMessage extends BaseMessage {
  trans_id?: number;
}

/**
 * Configuration for the WebSocket client
 */
export interface WebSocketClientConfig {
  /** WebSocket URL (may include query parameters like token) */
  url: string;
  /** Request timeout in milliseconds (default: 30000) */
  requestTimeout?: number;
  /** Reconnection delay in milliseconds (default: 5000) */
  reconnectDelay?: number;
  /** Maximum reconnection attempts (default: Infinity) */
  maxReconnectAttempts?: number;
  /** Enable debug logging (default: false) */
  debug?: boolean;
  /** Optional Zod schema for validating incoming messages */
  responseSchema?: ZodType<any>;
  /** Enable strict validation (throw on validation errors, default: false) */
  strictValidation?: boolean;
  /** 
   * Optional authentication token to include in WebSocket connection
   * Note: Browser WebSocket API doesn't support custom headers, so token
   * must be passed via query parameter or subprotocol
   */
  authToken?: string;
}

/**
 * Error types for WebSocket operations
 */
export class WebSocketError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = "WebSocketError";
  }
}

/**
 * Base AsyncAPI WebSocket client
 *
 * Generic type parameters:
 * - TRequest: Union type of all client → server operations
 * - TResponse: Union type of all server → client responses
 * - TPush: Union type of server → client push notifications (no trans_id)
 */
export abstract class BaseAsyncAPIClient<
  TRequest extends RequestMessage,
  TResponse extends ResponseMessage,
  TPush extends BaseMessage
> {
  /** Current connection state */
  readonly state$ = new BehaviorSubject<WebSocketClientState>(
    WebSocketClientState.INITIAL
  );

  /** All messages received from server (for debugging/logging) */
  readonly messages$ = new Subject<TResponse | TPush>();

  /** Push notifications from server (no trans_id) */
  readonly pushNotifications$ = new Subject<TPush>();

  /** WebSocket instance */
  private ws: WebSocket | null = null;

  /** Sequential transaction ID counter */
  private transId = 0;

  /** Pending requests awaiting responses */
  private pendingRequests = new Map<
    number,
    {
      resolve: (response: TResponse) => void;
      reject: (error: Error) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();

  /** Reconnection attempt counter */
  private reconnectAttempts = 0;

  /** Configuration */
  protected config: Required<Omit<WebSocketClientConfig, 'responseSchema' | 'strictValidation' | 'authToken'>> & Pick<WebSocketClientConfig, 'responseSchema' | 'strictValidation' | 'authToken'>;

  constructor(config: WebSocketClientConfig) {
    this.config = {
      requestTimeout: 30000,
      reconnectDelay: 5000,
      maxReconnectAttempts: Infinity,
      debug: false,
      strictValidation: false,
      ...config,
    };
  }

  /**
   * Connect to the WebSocket server
   */
  async connect(): Promise<void> {
    if (
      this.state$.value === WebSocketClientState.CONNECTED ||
      this.state$.value === WebSocketClientState.CONNECTING
    ) {
      this.log("Already connected or connecting");
      return;
    }

    this.state$.next(WebSocketClientState.CONNECTING);
    this.log(`Connecting to ${this.config.url}`);

    return new Promise((resolve, reject) => {
      let connectionRejected = false;
      const rejectOnce = (error: Error) => {
        if (!connectionRejected) {
          connectionRejected = true;
          reject(error);
        }
      };

      try {
        // Construct WebSocket URL with token if provided
        let wsUrl = this.config.url;
        if (this.config.authToken) {
          // Add token as query parameter
          const separator = wsUrl.includes('?') ? '&' : '?';
          wsUrl = `${wsUrl}${separator}token=${encodeURIComponent(this.config.authToken)}`;
          this.log(`Connecting with authentication token`);
        }

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.log("WebSocket connection established");
          this.state$.next(WebSocketClientState.CONNECTED);
          this.reconnectAttempts = 0;
          this.onConnected();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
          const errorDetails = {
            type: error.type,
            target: error.target,
            url: this.config.url,
            readyState: this.ws?.readyState,
          };
          this.log("WebSocket error:", errorDetails);
          
          if (this.state$.value === WebSocketClientState.CONNECTING) {
            // Get more details from the close event if available
            const closeCode = this.ws?.readyState === WebSocket.CLOSED ? 'CLOSED' : 'UNKNOWN';
            rejectOnce(
              new WebSocketError(
                `Failed to connect to ${this.config.url}. Error: ${closeCode}`,
                "CONNECTION_FAILED",
                errorDetails
              )
            );
          }
        };

        this.ws.onclose = (event) => {
          const closeDetails = {
            code: event.code,
            reason: event.reason || 'No reason provided',
            wasClean: event.wasClean,
            url: this.config.url,
          };
          
          // Map close codes to human-readable messages
          const closeCodeMessages: Record<number, string> = {
            1000: 'Normal closure',
            1001: 'Going away',
            1002: 'Protocol error',
            1003: 'Unsupported data',
            1004: 'Reserved',
            1005: 'No status code',
            1006: 'Abnormal closure (connection lost without close frame)',
            1007: 'Invalid frame payload data',
            1008: 'Policy violation',
            1009: 'Message too big',
            1010: 'Missing extension',
            1011: 'Internal error',
            1012: 'Service restart',
            1013: 'Try again later',
            1014: 'Bad gateway',
            1015: 'TLS handshake failure',
          };

          const codeMessage = closeCodeMessages[event.code] || `Unknown code: ${event.code}`;
          this.log(`WebSocket closed: ${event.code} (${codeMessage}) - ${event.reason || 'No reason'}`);
          
          // If connection was rejected during initial connection, reject the promise
          if (this.state$.value === WebSocketClientState.CONNECTING) {
            rejectOnce(
              new WebSocketError(
                `WebSocket connection failed: ${codeMessage}. Code: ${event.code}${event.reason ? `, Reason: ${event.reason}` : ''}`,
                "CONNECTION_FAILED",
                closeDetails
              )
            );
          } else {
            // Otherwise, handle as disconnection
            this.handleDisconnection();
          }
        };
      } catch (error) {
        this.state$.next(WebSocketClientState.FAILED);
        rejectOnce(error as Error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.log("Disconnecting...");
    this.state$.next(WebSocketClientState.DISCONNECTED);

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject, timer }) => {
      clearTimeout(timer);
      reject(new WebSocketError("Client disconnected", "DISCONNECTED"));
    });
    this.pendingRequests.clear();

    // Close WebSocket
    if (this.ws) {
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }

    // Reset trans_id for next connection
    this.transId = 0;
  }

  /**
   * Send a request and wait for response
   *
   * @param operation The operation message to send
   * @param timeoutMs Optional timeout override
   * @returns Promise that resolves with the response
   */
  async request<T extends TResponse>(
    operation: Omit<TRequest, "trans_id">,
    timeoutMs?: number
  ): Promise<T> {
    if (this.state$.value !== WebSocketClientState.CONNECTED) {
      throw new WebSocketError(
        "Client not connected",
        "NOT_CONNECTED",
        this.state$.value
      );
    }

    const trans_id = this.getNextTransId();
    const message = { ...operation, trans_id } as TRequest;

    return new Promise((resolve, reject) => {
      // Set up timeout
      const timer = setTimeout(() => {
        this.pendingRequests.delete(trans_id);
        reject(
          new WebSocketError(
            `Request timed out after ${timeoutMs || this.config.requestTimeout}ms`,
            "TIMEOUT",
            { trans_id, message: operation.message }
          )
        );
      }, timeoutMs || this.config.requestTimeout);

      // Store pending request
      this.pendingRequests.set(trans_id, {
        resolve: resolve as (response: TResponse) => void,
        reject,
        timer,
      });

      // Send message
      this.send(message);
    });
  }

  /**
   * Send a message without waiting for response (fire-and-forget)
   */
  send(message: TRequest): void {
    if (this.state$.value !== WebSocketClientState.CONNECTED) {
      throw new WebSocketError(
        "Client not connected",
        "NOT_CONNECTED",
        this.state$.value
      );
    }

    if (!this.ws) {
      throw new WebSocketError("WebSocket instance is null", "NO_WEBSOCKET");
    }

    const json = JSON.stringify(message);
    this.log("Sending:", message);
    this.ws.send(json);
  }

  /**
   * Get the next sequential transaction ID
   */
  private getNextTransId(): number {
    const tid = this.transId;
    this.transId++;
    return tid;
  }

  /**
   * Handle incoming message from server
   */
  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data) as TResponse | TPush;
      this.log("Received:", message);

      // Optional Zod validation
      if (this.config.responseSchema) {
        const validation = this.config.responseSchema.safeParse(message);
        if (!validation.success) {
          this.log("Validation error:", validation.error);
          if (this.config.strictValidation) {
            throw new WebSocketError(
              "Message validation failed",
              "VALIDATION_ERROR",
              validation.error
            );
          }
          // In non-strict mode, log warning but continue processing
          console.warn("[WebSocket] Message validation failed:", validation.error.format());
        }
      }

      // Emit to all messages stream
      this.messages$.next(message as TResponse);

      // Check if this is a response to a pending request
      if ("trans_id" in message && typeof message.trans_id === "number") {
        const pending = this.pendingRequests.get(message.trans_id);
        if (pending) {
          clearTimeout(pending.timer);
          this.pendingRequests.delete(message.trans_id);
          pending.resolve(message as TResponse);
          return;
        }
      }

      // Otherwise, treat as push notification
      this.pushNotifications$.next(message as TPush);

      // Call subclass handler
      this.onPushNotification(message as TPush);
    } catch (error) {
      this.log("Error parsing message:", error);
    }
  }

  /**
   * Handle disconnection and attempt reconnection
   */
  private handleDisconnection(): void {
    if (this.state$.value === WebSocketClientState.DISCONNECTED) {
      // Intentional disconnect, don't reconnect
      return;
    }

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject, timer }) => {
      clearTimeout(timer);
      reject(new WebSocketError("Connection lost", "CONNECTION_LOST"));
    });
    this.pendingRequests.clear();

    // Attempt reconnection if not exceeded max attempts
    if (this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this.state$.next(WebSocketClientState.RECONNECTING);
      this.reconnectAttempts++;

      this.log(
        `Reconnecting in ${this.config.reconnectDelay}ms (attempt ${this.reconnectAttempts})`
      );

      setTimeout(() => {
        this.connect().catch((error) => {
          this.log("Reconnection failed:", error);
          if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
            this.state$.next(WebSocketClientState.FAILED);
          }
        });
      }, this.config.reconnectDelay);
    } else {
      this.state$.next(WebSocketClientState.FAILED);
      this.log("Max reconnection attempts exceeded");
    }
  }

  /**
   * Log debug messages if enabled
   */
  protected log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log(`[${this.constructor.name}]`, ...args);
    }
  }

  /**
   * Called when connection is established (override in subclass for setup)
   */
  protected abstract onConnected(): void;

  /**
   * Called when a push notification is received (override in subclass)
   */
  protected abstract onPushNotification(message: TPush): void;

  /**
   * Get an observable that filters messages by type
   */
  protected messagesOfType<T extends TPush>(
    messageType: string
  ): Observable<T> {
    return this.pushNotifications$.pipe(
      filter((msg) => msg.message === messageType)
    ) as Observable<T>;
  }
}
